/**
 * DataGridToolbar Component
 *
 * Composable toolbar for DataGrid following Fluent UI v9 patterns.
 * Uses children composition for maximum flexibility.
 *
 * Usage:
 * ```tsx
 * <DataGridToolbar>
 *   <DataGridToolbar.Left>
 *     <ToolbarButton icon={<RefreshIcon />} onClick={refetch}>Refresh</ToolbarButton>
 *     <CustomComponent />
 *   </DataGridToolbar.Left>
 *   <DataGridToolbar.Right>
 *     <ToolbarSearch value={search} onChange={setSearch} />
 *     <ToolbarButton icon={<DeleteIcon />} onClick={handleDelete}>Delete</ToolbarButton>
 *     <ToolbarButton appearance="primary" icon={<AddIcon />} onClick={handleAdd}>Add</ToolbarButton>
 *   </DataGridToolbar.Right>
 * </DataGridToolbar>
 * ```
 */

import { ReactNode } from 'react';
import {
  ButtonProps,
  Input,
  InputProps,
  makeStyles,
  shorthands,
  tokens,
  mergeClasses,
} from '@fluentui/react-components';
import { Search20Regular } from '@fluentui/react-icons';
import { AsyncButton } from '../../common/AsyncButton';

const useStyles = makeStyles({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('0', '0', '12px', '0'),
  },
  left: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
  button: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('4px'),
  },
  searchBox: {
    width: '240px',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('4px'),
  },
});

// ============================================================================
// DataGridToolbar (Container)
// ============================================================================

interface DataGridToolbarProps {
  /** Toolbar content - use DataGridToolbar.Left and DataGridToolbar.Right */
  children: ReactNode;
  /** Additional CSS class */
  className?: string;
}

function DataGridToolbarRoot({ children, className }: DataGridToolbarProps) {
  const styles = useStyles();
  return (
    <div className={mergeClasses(styles.toolbar, className)}>
      {children}
    </div>
  );
}

// ============================================================================
// DataGridToolbar.Left
// ============================================================================

interface ToolbarSectionProps {
  /** Section content */
  children: ReactNode;
  /** Additional CSS class */
  className?: string;
}

function ToolbarLeft({ children, className }: ToolbarSectionProps) {
  const styles = useStyles();
  return (
    <div className={mergeClasses(styles.left, className)}>
      {children}
    </div>
  );
}

// ============================================================================
// DataGridToolbar.Right
// ============================================================================

function ToolbarRight({ children, className }: ToolbarSectionProps) {
  const styles = useStyles();
  return (
    <div className={mergeClasses(styles.right, className)}>
      {children}
    </div>
  );
}

// ============================================================================
// ToolbarButton - Styled button for use in toolbar
// ============================================================================

interface ToolbarButtonProps {
  /** Button appearance - defaults to subtle with border styling */
  appearance?: 'primary' | 'subtle';
  /** Button icon */
  icon?: ButtonProps['icon'];
  /** Click handler */
  onClick?: () => void;
  /** Disabled state */
  disabled?: boolean;
  /** Button content */
  children?: ReactNode;
  /** Additional CSS class */
  className?: string;
  /** Test ID */
  'data-testid'?: string;
  /** Loading state - shows spinner and disables interaction */
  loading?: boolean;
  /** Text to display while loading */
  loadingText?: string;
}

export function ToolbarButton({
  appearance = 'subtle',
  icon,
  onClick,
  disabled,
  children,
  className,
  'data-testid': testId,
  loading,
  loadingText,
}: ToolbarButtonProps) {
  const styles = useStyles();

  // Primary buttons don't get the bordered style
  const buttonClass = appearance === 'primary'
    ? className
    : mergeClasses(styles.button, className);

  // Use AsyncButton for loading state support
  return (
    <AsyncButton
      appearance={appearance}
      icon={icon}
      onClick={onClick}
      disabled={disabled}
      className={buttonClass}
      data-testid={testId}
      loading={loading}
      loadingText={loadingText}
    >
      {children}
    </AsyncButton>
  );
}

// ============================================================================
// ToolbarSearch - Styled search input for use in toolbar
// ============================================================================

interface ToolbarSearchProps extends Omit<InputProps, 'onChange'> {
  /** Current search value */
  value: string;
  /** Callback when search value changes */
  onChange: (value: string) => void;
  /** Placeholder text */
  placeholder?: string;
}

export function ToolbarSearch({
  value,
  onChange,
  placeholder = 'Search...',
  className,
  ...props
}: ToolbarSearchProps) {
  const styles = useStyles();

  return (
    <Input
      placeholder={placeholder}
      contentBefore={<Search20Regular />}
      value={value}
      onChange={(_e, data) => onChange(data.value)}
      className={mergeClasses(styles.searchBox, className)}
      {...props}
    />
  );
}

// ============================================================================
// Compound Component Export
// ============================================================================

export const DataGridToolbar = Object.assign(DataGridToolbarRoot, {
  Left: ToolbarLeft,
  Right: ToolbarRight,
});

export type { DataGridToolbarProps, ToolbarSectionProps, ToolbarButtonProps, ToolbarSearchProps };
